import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { minorToStr } from "@/lib/money";

const ReportChart = lazy(() => import("@/components/ReportChart"));
export const PL_COLS = [["revenue", "Revenue (ex tax)"], ["cogs", "Cost of goods"], ["profit", "Gross profit"], ["margin_bp", "Margin"], ["nocost", "Lines without cost"]];
const SERIES = [["revenue", "Revenue", "#0f172a"], ["cogs", "Cost of goods", "#f59e0b"], ["profit", "Gross profit", "#10b981"]];
const pct = (bp) => (bp == null ? "—" : `${(bp / 100).toFixed(1)}%`);

function download(data, cell, label) {
  const head = ["Period", "Start", "End", ...PL_COLS.map((c) => c[1])];
  const lines = [head, ...data.rows.map((r) => [label(r), r.start, r.end, ...PL_COLS.map(([k]) => cell(r, k))]), ["Total", data.start, data.end, ...PL_COLS.map(([k]) => cell(data.totals, k))],
    [], ["Top products by profit"], ["Product", "Qty sold", "Qty returned", "Revenue (ex tax)", "Cost of goods", "Gross profit", "Margin"],
    ...data.top_products.map((p) => [p.name, p.qty, p.returned_qty, cell(p, "revenue"), cell(p, "cogs"), cell(p, "profit"), pct(p.margin_bp)])];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" }));
  a.download = `profit-loss-${data.start}-to-${data.end}-${data.group}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Stat({ id, label, value, cur, tone }) {
  return (
    <div className={`min-w-0 rounded-2xl border p-4 ${tone || "border-slate-200 bg-white"}`} data-testid={`pl-total-${id}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 break-all font-mono text-base font-bold leading-tight sm:text-lg" data-testid={`pl-total-${id}-value`}>{cur && <span className="mr-1 text-[11px] font-semibold opacity-60">{cur}</span>}{value}</div>
    </div>
  );
}

export function ProfitLoss({ start, end, group, branch, valid, label, onError }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!valid) return;
    setBusy(true);
    api.get("/reports/pl", { params: { start, end, group, branch } }).then((r) => { setData(r.data); onError(""); })
      .catch((e) => onError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't load the report")).finally(() => setBusy(false));
  }, [start, end, group, branch, valid, onError]);
  const fmt = useMemo(() => (n) => minorToStr(n || 0, data?.decimals ?? 2), [data]);
  const cell = (r, k) => (k === "margin_bp" ? pct(r[k]) : k === "nocost" ? r[k] : fmt(r[k]));
  if (!data) return busy ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : null;
  const t = data.totals;
  return (
    <div className={`space-y-4 ${busy ? "opacity-60" : ""}`} data-testid="pl-view">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-slate-600">Revenue excludes tax, net of discounts and returns, including delivery fees. Resellable returns give their cost back; damaged returns stay as cost.</p>
        <button onClick={() => download(data, cell, label)} data-testid="pl-export" className="ml-auto flex h-11 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 text-sm font-bold"><Download className="h-4 w-4" />Export P&amp;L CSV</button>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="pl-totals">
        <Stat id="revenue" label="Revenue (ex tax)" cur={data.currency} value={fmt(t.revenue)} />
        <Stat id="cogs" label="Cost of goods" cur={data.currency} value={fmt(t.cogs)} />
        <Stat id="profit" label="Gross profit" cur={data.currency} value={fmt(t.profit)} tone={t.profit < 0 ? "border-rose-300 bg-rose-50 text-rose-800" : "border-slate-900 bg-slate-900 text-white"} />
        <Stat id="margin" label="Margin" value={pct(t.margin_bp)} />
        <Stat id="nocost" label="Lines without cost" value={t.nocost} tone={t.nocost ? "border-amber-300 bg-amber-50 text-amber-900" : undefined} />
      </div>
      {t.nocost > 0 && <p className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="pl-nocost-note"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{t.nocost} sales {t.nocost === 1 ? "line" : "lines"} without cost. Add a Cost price to those products (or receive them with a cost) so profit is complete.</p>}
      <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4" data-testid="pl-chart">
        <div className="mb-2 text-sm font-bold">Revenue, cost and profit</div>
        <Suspense fallback={<div className="h-64" />}><ReportChart rows={data.rows.map((r) => ({ label: label(r), revenue: r.revenue, cogs: r.cogs, profit: r.profit }))} decimals={data.decimals} series={SERIES} testid="pl-chart-canvas" /></Suspense>
      </div>
      <PlTable data={data} cell={cell} label={label} />
      <TopProducts data={data} fmt={fmt} />
    </div>
  );
}

function PlTable({ data, cell, label }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white"><div className="overflow-x-auto">
      <table className="w-full min-w-[680px] text-right text-sm" data-testid="pl-table">
        <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="sticky left-0 bg-slate-50 px-3 py-2.5 text-left">Period</th>{PL_COLS.map(([k, l]) => <th key={k} className="px-3 py-2.5">{l}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100 font-mono text-xs sm:text-sm">
          {data.rows.map((r) => <tr key={r.key} data-testid={`pl-row-${r.key}`}><td className="sticky left-0 bg-white px-3 py-2 text-left font-sans font-semibold">{label(r)}</td>{PL_COLS.map(([k]) => <td key={k} className={`px-3 py-2 ${k === "profit" ? "font-bold" : ""} ${k === "profit" && r[k] < 0 ? "text-rose-600" : ""}`}>{cell(r, k)}</td>)}</tr>)}
        </tbody>
        <tfoot className="border-t-2 border-slate-900 bg-slate-50 font-mono font-bold"><tr data-testid="pl-row-total"><td className="sticky left-0 bg-slate-50 px-3 py-2.5 text-left font-sans">Total</td>{PL_COLS.map(([k]) => <td key={k} className="px-3 py-2.5">{cell(data.totals, k)}</td>)}</tr></tfoot>
      </table>
    </div></div>
  );
}

function TopProducts({ data, fmt }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="pl-top-products">
      <div className="px-4 pt-4 text-sm font-bold">Top 10 products by profit</div>
      {!data.top_products.length && <p className="p-4 text-sm text-slate-500" data-testid="pl-top-empty">No sales in this range.</p>}
      <ol className="divide-y divide-slate-100">
        {data.top_products.map((p, i) => (
          <li key={p.product_id} className="flex items-center gap-3 px-4 py-3" data-testid={`pl-top-${i}`}>
            <span className="w-5 shrink-0 font-mono text-xs text-slate-400">{i + 1}</span>
            <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{p.name}</div>
              <div className="font-mono text-[11px] text-slate-500">{p.qty} sold{p.returned_qty ? ` · ${p.returned_qty} returned` : ""} · revenue {fmt(p.revenue)} · cost {fmt(p.cogs)}{p.nocost ? ` · ${p.nocost} without cost` : ""}</div></div>
            <div className="text-right"><div className={`font-mono text-sm font-bold ${p.profit < 0 ? "text-rose-600" : ""}`} data-testid={`pl-top-${i}-profit`}>{data.currency} {fmt(p.profit)}</div>
              <div className="font-mono text-[11px] text-slate-500">{pct(p.margin_bp)}</div></div>
          </li>))}
      </ol>
    </div>
  );
}
